/**
 * Hook for automatic calculations of expense invoice item totals
 * Calculates totalNet, totalVat, totalGross based on quantity, unitNet, unitGross, vatRate
 */

import { useMemo } from 'react';
import type { ExpenseInvoiceItemFormData } from '../Validation/expenseInvoiceValidation';

export interface CalculatedTotals {
  totalNet: number;
  totalVat: number;
  totalGross: number;
}

/**
 * Calculate totals for a single invoice item
 */
export const calculateItemTotals = (
  item: Partial<ExpenseInvoiceItemFormData>
): CalculatedTotals => {
  const quantity = item.quantity || 0;
  const unitNet = item.unitNet || 0;
  const unitGross = item.unitGross || 0;

  const totalNet = quantity * unitNet;
  const totalGross = quantity * unitGross;
  const totalVat = totalGross - totalNet;

  return {
    totalNet: Math.round(totalNet * 100) / 100,
    totalVat: Math.round(totalVat * 100) / 100,
    totalGross: Math.round(totalGross * 100) / 100,
  };
};

/**
 * Calculate invoice summary totals from all items
 */
export const calculateInvoiceTotals = (
  items: Partial<ExpenseInvoiceItemFormData>[]
): CalculatedTotals => {
  const totals = items.reduce(
    (acc, item) => {
      const itemTotals = calculateItemTotals(item);
      return {
        totalNet: acc.totalNet + itemTotals.totalNet,
        totalVat: acc.totalVat + itemTotals.totalVat,
        totalGross: acc.totalGross + itemTotals.totalGross,
      };
    },
    { totalNet: 0, totalVat: 0, totalGross: 0 }
  );

  return {
    totalNet: Math.round(totals.totalNet * 100) / 100,
    totalVat: Math.round(totals.totalVat * 100) / 100,
    totalGross: Math.round(totals.totalGross * 100) / 100,
  };
};

/**
 * Hook for calculating expense invoice totals
 * Memoized to avoid recalculation on every render
 */
export const useExpenseInvoiceCalculations = (items: Partial<ExpenseInvoiceItemFormData>[]) => {
  const totals = useMemo(() => calculateInvoiceTotals(items), [items]);

  return {
    totalNet: totals.totalNet,
    totalVat: totals.totalVat,
    totalGross: totals.totalGross,
    formatCurrency: (value: number, currency: string = 'PLN') => `${value.toFixed(2)} ${currency}`,
  };
};
